'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { VisuallyHidden } from '@/components/ui/visually-hidden';
import { cn } from '@/lib/utils';
import { CreateMatterFormView } from '@/components/quick-actions/create-matter-form-view';
import { MatterType } from '@/lib/services/matter-types/types';

interface CreateMatterFormModalProps {
  matterType: MatterType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * CreateMatterFormModal Component
 *
 * Modal that wraps the CreateMatterFormView for creating non-Estate Administration matters.
 * Reuses the same form component from Quick Actions.
 */
export function CreateMatterFormModal({
  matterType,
  open,
  onOpenChange
}: CreateMatterFormModalProps) {
  const handleSuccess = () => {
    onOpenChange(false);
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        {/* Transparent overlay */}
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-transparent" />

        {/* Modal content with custom styling */}
        <DialogPrimitive.Content
          className={cn(
            'fixed top-[50%] left-[50%] z-50 w-full max-w-3xl translate-x-[-50%] translate-y-[-50%]',
            'overflow-hidden rounded-xl p-0',
            'bg-[linear-gradient(0deg,#FAFAFA_0%,#FAFAFA_100%),#FFF]',
            'shadow-[0_8px_28px_-6px_rgba(31,28,29,0.12),0_18px_88px_-4px_rgba(31,28,29,0.16)]'
          )}
        >
          {/* Close button */}
          <DialogPrimitive.Close className="focus:ring-ring/70 absolute top-4 right-4 z-10 cursor-pointer rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:outline-hidden">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>

          {/* Hidden title for accessibility */}
          <VisuallyHidden>
            <DialogPrimitive.Title>
              Create {matterType.label} Matter
            </DialogPrimitive.Title>
          </VisuallyHidden>

          {/* Form view */}
          <div className="bg-surface-subtle max-h-[85vh] overflow-y-auto">
            <CreateMatterFormView
              visible={open}
              matterType={matterType}
              onBack={() => onOpenChange(false)}
              onSuccess={handleSuccess}
              hideBreadcrumb
            />
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
